import React from "react";
import { find, get, map, sumBy } from "lodash";
import DemographyChart from "@charty/demography-chart";
import { getAllDemographicRanges } from "@linkfluence/models/demographic-range";
import { DemographicRange } from "@linkfluence/models/demographic-range";
import { Gender } from "@linkfluence/models/gender";
import { Metric } from "@linkfluence/models/metric";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { DatavizDemographic } from "@radarly/models/dataviz";
import { SearchFragmentDemographic } from "@radarly/models/search";
import Empty from "../../empty";
import Tooltip from "../../tooltip/metric";

export interface SharedWidgetsDemographicPyramidInnerProps {
  data: SearchFragmentDemographic["data"];
  fragment: SearchFragmentDemographic;
  dataviz: DatavizDemographic;
  className?: string;
  onClickRange?: (range: string) => void;
  onClickRangeGender?: (range: string, gender: string) => void;
  disableAnimation?: boolean;
}

const getValueFromGenderAndRange = (
  data: SearchFragmentDemographic["data"]["aggs"]["genders"]["values"],
  gender: Gender.Male | Gender.Female,
  range: DemographicRange,
  metric: Metric
) => {
  const genderData = find(data, { key: gender });
  if (genderData) {
    const ageData = find(genderData.aggs.age.values, { key: range });
    return ageData ? ageData.counts[metric] : 0;
  } else {
    return 0;
  }
};

const formatData = (
  data: SearchFragmentDemographic["data"]["aggs"]["genders"]["values"],
  metric: Metric
) => {
  return map(getAllDemographicRanges(), (range) => {
    return {
      range,
      data: {
        [Gender.Male]: getValueFromGenderAndRange(
          data,
          Gender.Male,
          range,
          metric
        ),
        [Gender.Female]: getValueFromGenderAndRange(
          data,
          Gender.Female,
          range,
          metric
        ),
      },
    };
  });
};

const isEmptyData = (data) =>
  sumBy(data, (d) => get(d, "data." + Gender.Male, 0)) +
    sumBy(data, (d) => get(d, "data." + Gender.Female, 0)) ===
  0;

export default function SharedWidgetsDemographicPyramidInner({
  data,
  fragment,
  onClickRange,
  onClickRangeGender,
  disableAnimation,
}: SharedWidgetsDemographicPyramidInnerProps) {
  const locale = useCurrentUserLocale();
  const metric = fragment.request.aggs.genders.aggs.age.metrics[0];
  const series = React.useMemo(() => {
    return formatData(data ? data.aggs.genders.values : [], metric);
  }, [data, metric]);
  return (
    <Empty empty={isEmptyData(series)}>
      <DemographyChart
        data={series}
        locale={locale}
        tooltip={(serie) => (
          <Tooltip
            count={serie.count}
            color={serie.color}
            label={serie.label}
            metric={metric}
            locale={locale}
          />
        )}
        interactionSelectRange={onClickRange}
        interactionSelectRangeValue={onClickRangeGender}
        disableAnimation={disableAnimation}
      />
    </Empty>
  );
}
